import type { ReactNode } from 'react';
import {
  prepareInlineMarkupRuns,
  renderPreparedMarkupRuns,
} from './prepared-inline-markup';

/**
 * Renders authored text with its `{…|…}` markers resolved — marks as elements,
 * indexes against a link list, `.n` against a styling list, `{br|}` as a line
 * break.
 *
 * Every element is phrasing content and sits inside whatever tag the caller
 * wraps this in; nothing here ever becomes the caller's root element, so the
 * wrapper decisions in `MINIMAL_MARKUP_ARCHITECTURE.md` are unaffected.
 *
 * When no run needs an element the return value is a plain string: the same
 * React output as text with no markup support at all. A span whose link or
 * styling entry is missing, cleared, or rejected contributes its text, so a
 * broken reference degrades to prose rather than leaving a `{1|…}` shell,
 * emitting an `<a>` without an `href`, or wrapping words in a `<span>` that
 * carries nothing.
 *
 * `fillText`, when given, rewrites the words of every run once the markup is
 * parsed — `r-text` passes one that fills `{{ … }}` placeholders. What it
 * returns is only ever text, so a filled-in value cannot become markup, and a
 * span whose words come back empty renders nothing rather than an empty `<a>`
 * or `<strong>`. Without it, placeholders show exactly as written.
 */
export function renderInlineMarkup(
  text: string,
  links: unknown,
  spanStyles?: unknown,
  fillText?: (words: string) => string
): ReactNode {
  return renderPreparedMarkupRuns(
    prepareInlineMarkupRuns(text, links, spanStyles, fillText)
  );
}
